import { Trans, useLingui } from '@lingui/react/macro';

import Box from '@/design_system/Box';
import { InputMultiSelect } from '@/design_system/InputMultiSelect/InputMultiSelect';
import Stack from '@/design_system/Stack';
import Paragraph from '@/design_system/Typography/Paragraph';
import {
  getChildProductAttributesByParentValueId,
  getRootProductAttributes,
  getSelectedProductAttributeValueIdsWithParents,
  useProductAttributes,
} from '@/models/productAttribute';
import { ProductAttributeValueWithParent } from '@/models/request';
import { useCurrentOrganization } from '@/services/auth';
import { useDatabaseTranslation } from '@/services/i18n';

const INDENTATION_PX = 8;

export const ProductAttributeValuesMultiSelects = ({
  productAttributeValues,
  setProductAttributeValues,
}: {
  productAttributeValues: ProductAttributeValueWithParent[];
  setProductAttributeValues: (values: ProductAttributeValueWithParent[]) => void;
}) => {
  const { t } = useLingui();
  const { _db } = useDatabaseTranslation();
  const [organization] = useCurrentOrganization();
  const { data: { productAttributes = [] } = {} } = useProductAttributes({
    organizationId: organization?.id,
  });

  const allOptions: ProductAttributeValueWithParent[] = productAttributes.flatMap(
    (productAttribute) => productAttribute.productAttributeValues
  );
  const optionsById = Object.fromEntries(allOptions.map((option) => [option.id, option]));
  const rootProductAttributes = getRootProductAttributes({ productAttributes });
  const childAttributesByParentValueId = getChildProductAttributesByParentValueId({
    productAttributes,
  });

  const getSelectedValuesForAttribute = (productAttributeId: string) =>
    productAttributeValues.filter((value) => value.productAttribute.id === productAttributeId);

  const setSelectedValueIds = (nextSelectedValueIds: string[]) => {
    const validSelectedValueIds = getSelectedProductAttributeValueIdsWithParents({
      productAttributes,
      selectedValueIds: nextSelectedValueIds,
    });

    setProductAttributeValues(
      validSelectedValueIds
        .map((selectedValueId) => optionsById[selectedValueId])
        .filter((value): value is ProductAttributeValueWithParent => !!value)
    );
  };

  const handleAttributeChange = ({
    productAttributeId,
    nextValues,
  }: {
    productAttributeId: string;
    nextValues: ProductAttributeValueWithParent[];
  }) => {
    const selectedValueIdsForOtherAttributes = productAttributeValues
      .filter((value) => value.productAttribute.id !== productAttributeId)
      .map((value) => value.id);

    setSelectedValueIds([
      ...selectedValueIdsForOtherAttributes,
      ...nextValues.map((value) => value.id),
    ]);
  };

  const renderAttributeSelect = ({
    productAttributeId,
    parentValueName,
    depth = 0,
  }: {
    productAttributeId: string;
    parentValueName?: string;
    depth?: number;
  }) => {
    const productAttribute = productAttributes.find(
      (attribute) => attribute.id === productAttributeId
    );

    if (!productAttribute) {
      return null;
    }

    const selectedValuesForAttribute = getSelectedValuesForAttribute(productAttribute.id);
    const productAttributeName = _db(productAttribute.name);
    const label = parentValueName
      ? t({
          id: 'settings.shared.product-attribute-values.input-multi-select.label',
          message: `${productAttributeName} for ${parentValueName}`,
        })
      : productAttributeName;

    return (
      <Stack
        gap="0.5rem"
        key={`${productAttribute.id}-${parentValueName ?? 'root'}`}
        style={{ paddingLeft: `${depth * INDENTATION_PX}px` }}
      >
        <InputMultiSelect
          variant="select"
          label={label}
          placeholder={label}
          value={selectedValuesForAttribute}
          options={productAttribute.productAttributeValues}
          getOptionValue={(option) => option.id}
          getOptionLabel={(option) => _db(option.name)}
          isSearchable
          onChange={(values) =>
            handleAttributeChange({
              productAttributeId: productAttribute.id,
              nextValues: values.map((value) => value),
            })
          }
          onRemoveValue={(optionId) =>
            handleAttributeChange({
              productAttributeId: productAttribute.id,
              nextValues: selectedValuesForAttribute.filter((value) => value.id !== optionId),
            })
          }
          isDisabled={!productAttribute.productAttributeValues.length}
        />
        {selectedValuesForAttribute.map((selectedValue) =>
          (childAttributesByParentValueId[selectedValue.id] ?? []).map((childAttribute) =>
            renderAttributeSelect({
              productAttributeId: childAttribute.id,
              parentValueName: _db(selectedValue.name),
              depth: depth + 1,
            })
          )
        )}
      </Stack>
    );
  };

  return (
    <Box gap="0.5rem">
      <Paragraph size="100" weight="medium">
        <Trans id="settings.shared.product-attribute-values.label">Product attribute values</Trans>
      </Paragraph>
      {!allOptions.length
        ? null
        : rootProductAttributes.map((attribute) =>
            renderAttributeSelect({ productAttributeId: attribute.id })
          )}
    </Box>
  );
};
